import { formatNumber } from '@angular/common';
import { Inject,Injectable,LOCALE_ID } from '@angular/core';
import { FormArray,FormControl,FormGroup,Validators } from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
import moment,{ Moment } from 'moment';
import { combineLatest,Observable,of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, tap } from 'rxjs/operators';
import { AccountsReceivableLedgerEntity,AccountsReceivableLedgerTypeEnum,CalcAccountsReceivableLedgerTaxAmount,NettoCost } from '../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { compareEqualityAsJsonString,currencyValues,isNotNullOrUndefined,uniqArray } from '../../../helper/helper';
import { aRLBaseCostAllowancePercentValidator,decimalPriceValidator,decimalValidator } from '../../../helper/validation.helper';
import { getLedgerImportSums } from './arl-calculation.helper';
import { CalculationArlFormDataService,calculationArlFormGroup } from './calculation-arl-form-data.service';
import { FormControlCache } from './formcontrol-cache';

import * as mjsHelper from '../../../helper/math-js-helper';


class DebugFormControl<T> extends FormControl<T> {
    override setValue(value: any, options?: {
        onlySelf?: boolean;
        emitEvent?: boolean;
        emitModelToViewChange?: boolean;
        emitViewToModelChange?: boolean
    }) {
        console.trace('DebugFormControl setValue called with value:', value, 'and options:', options);
        super.setValue(value, options);
    }
}

export type arlBaseCostAllowanceFormGroup = FormGroup<{
    title: FormControl<string>;
    percent: FormControl<number>;
    arlType: FormControl<AccountsReceivableLedgerTypeEnum>;
    type: FormControl<arlBaseCostAllowanceType>;
}>;
export type arlQuantityHistoryFormGroup = FormGroup<{
    date: FormControl<Moment>;
    quantityDelta: FormControl<number>;
    quantityDeltaFormula: FormControl<string>;
}>;
export type arlGapFormGroup = FormGroup<{
   id: FormControl<number>;
   caption: FormControl<string>;
   body: FormControl<string>;
   indexInLongText: FormControl<number>;
}>;
export type arlBaseCostAllowanceType = 'allowance' | 'surcharge' | 'surchargeOverride';
export type arlTableDataArlForm = FormGroup<{
    // Amount: FormControl<number>;
    Information: FormControl<string>;
    BookingText: FormControl<string>;
    BaseCost: FormControl<number>;
    BaseCostGross: FormControl<number>;
    CostAmount: FormControl<number>;
    Quantity: FormControl<number>;
    QuantityTypeId: FormControl<number>;
    childrenVisible: FormControl<boolean>;
    InheritFromChildren: FormControl<boolean>;
    ParentId: FormControl<number | null>;
    ARLIds: FormControl<number[]>;
    ZuschlaegeResourcen: FormControl<number>;
    ZuschlaegeMaterials: FormControl<number>;
    ZuschlaegeDefault: FormControl<number>;
    ZuschlaegeEmployee: FormControl<number>;
    ZuschlaegeExternalService: FormControl<number>;
    ZuschlaegeOther: FormControl<number>;
    Type: FormControl<AccountsReceivableLedgerTypeEnum>;
    ResourceId: FormControl<number>;
    MaterialId: FormControl<number>;
    Longtext: FormControl<string>;
    ShowLongtext: FormControl<boolean>;
    Tax: FormControl<number>;
    AlreadyPayed: FormControl<number>;
    Date: FormControl<Moment>;
    IsOptional: FormControl<boolean>;
    CrossedOut: FormControl<boolean>;
    JobSpecificationId: FormControl<number>;
    SalaryBaseCost: FormControl<number>;
    SupplierId: FormControl<number>;
    StartOnNewPage: FormControl<boolean>;
    sonstigesBaseCost: FormControl<number>;
    childArls: FormArray<arlTableDataArlForm>;
    childArlsCalculation: FormArray<calculationArlFormGroup>;
    BaseCostAllowances: FormArray<arlBaseCostAllowanceFormGroup>;
    ItemNumber: FormControl<string>;
    HideInGroupSummary: FormControl<boolean | null>;
    Id: FormControl<number>;
    ShowQuantityHistory: FormControl<boolean>;
    UseQuantityHistory: FormControl<boolean>;
    QuantityHistory: FormArray<arlQuantityHistoryFormGroup>;
    lvCalculation: FormGroup<lvCalcForm>;
    QuantityFormula: FormControl<string>;
    QuantityFormulaUsedVariables: FormControl<string[]>;
    PriceComparisonId: FormControl<number>;
    ChangedAfterSelectedFromPriceComparison: FormControl<boolean>;
    Gaps: FormArray<arlGapFormGroup>;
    SurchargeOverride: FormControl<number | null>;
    FixedPrices: FormControl<boolean>;
}>;
const getARLQuantityHistoryFormArray = (a: AccountsReceivableLedgerEntity['QuantityHistory']) => {
    return new FormArray<arlQuantityHistoryFormGroup>((a || []).filter(x => x.quantityDelta).map(x => getARLQuantityHistoryForm(x)));
}
export const getARLQuantityHistoryForm = (a: AccountsReceivableLedgerEntity['QuantityHistory'][0]) =>
    new FormGroup({
        date: new FormControl<Moment>(moment(a.date)),
        quantityDelta: new FormControl<number>(a.quantityDelta),
        quantityDeltaFormula: new FormControl<string>(a.quantityDeltaFormula),
    });
// const getARLBaseCostAllowanceFormArray = (a: AccountsReceivableLedgerEntity['BaseCostAllowances']) => {
type lvCalcForm = {
    minutes:FormControl<number>;
    SoEk: FormControl<number>;
    SoAntEk: FormControl<number>;
    GEK: FormControl<number>;
    FlEk: FormControl<number>;
    PartieHEur: FormControl<number>;
    SoEkRabatt: FormControl<number>;
}
export const getARLBaseCostAllowanceFormArray = (a: AccountsReceivableLedgerEntity['BaseCostAllowances']) => {
    return new FormArray<arlBaseCostAllowanceFormGroup>((a || []).filter(x => x.percent).map(x => {
        return getARLBaseCostAllowanceForm(x, !!x.arlType ? 'surchargeOverride' : undefined, !!x.arlType);
    }));
}
export const getARLBaseCostAllowanceForm = (a: AccountsReceivableLedgerEntity['BaseCostAllowances'][0], type?: arlBaseCostAllowanceType, typeRequired = false): arlBaseCostAllowanceFormGroup => {
    const t = type ? type : a.percent > 0 ? 'surcharge' : 'allowance';

    return new FormGroup({
        title: new FormControl<string>(a.title),
        percent: new FormControl<number>(a.percent, {validators: [Validators.required, aRLBaseCostAllowancePercentValidator(t)]}),
        arlType: new FormControl<AccountsReceivableLedgerTypeEnum>(a.arlType, {validators: typeRequired ? [Validators.required] : undefined}),
        type: new FormControl<arlBaseCostAllowanceType>(t),
    })
}

@Injectable()
export class ArlFormDataService extends FormControlCache<arlTableDataArlForm> {
    public createNewFormsDisabled = false;
    constructor(@Inject(LOCALE_ID) private locale: string, private calculationArlFormDataService: CalculationArlFormDataService) {
        super();
    }

    /**
     *
     * @param id Id from arl to remove
     * @return Ids from all childArls including id
     */
    public removeArlWithChildren(id: number) {
        this.arlFormMap.forEach((form) => {
            if (form.value.ARLIds.includes(id)) {
                form.controls.ARLIds.setValue(form.value.ARLIds.filter((arlId) => arlId !== id));
                form.controls.ARLIds.markAsDirty();
            }
            const chiltIndex = form.value.childArls.findIndex((child) => child.Id === id);
            if (chiltIndex > -1) {
                form.controls.childArls.removeAt(chiltIndex);
                form.controls.childArls.markAsDirty();
            }
        });
        const childIds = this.getChildIds(id);
        childIds.forEach((arlId) => this.arlFormMap.delete(arlId));
        return childIds;
    }

    /**
     *
     * @param id Id from arl
     * @param excludeCalculationArls if true you will only get the children of groups
     * @return Ids from all childArls including id
     */
    public getChildIds = (id: number, excludeCalculationArls = true): number[] => {
        if (!this.arlFormMap.has(id)) {
            return [id];
        }
        const form = this.arlFormMap.get(id);
        // must be raw value!!! because of pending changes from ARLIds.valueChanges in getAmountSumForArls2$
        if (form.controls.ARLIds.getRawValue()?.length && (!excludeCalculationArls || form.controls.Type.getRawValue() === AccountsReceivableLedgerTypeEnum.Group)) {
            return [
                id,
                ...form
                    .getRawValue()
                    .ARLIds.map((arlId) => this.getChildIds(arlId))
                    .flat(),
            ];
        } else {
            return [id];
        }
    };

    public getFormByEntity(id: number, arlDict: Dictionary<AccountsReceivableLedgerEntity>): arlTableDataArlForm {
        const form = this.getForm(id);
        if (!form) {
            const arl = arlDict[id];
            if (!arl) {
                throw new Error(
                    `arl not found for id ${id}, arlDict: ${JSON.stringify(arlDict, null, 2)}`,
                )
            }
            this.setFormByEntity(arl, arlDict);
            return this.getForm(id);
        } else {
            return form;
        }
    }
    public setFormByEntity(e: AccountsReceivableLedgerEntity, arlDict?: Dictionary<AccountsReceivableLedgerEntity>) {
        // const e = arlDict[arlId];
        if (e.ArlIds?.length && !arlDict) {
            throw 'for ARLs with children ARL Dictionary is needed to init missing child forms';
        }
        const ArlIdsValue = e.ArlIds ? uniqArray(e.ArlIds) : [];
        const childArlsCalculation = new FormArray<calculationArlFormGroup>(
            e.Type !== AccountsReceivableLedgerTypeEnum.Group
                ? e.ArlIds.map((id) => {
                    const arl = arlDict[id];
                    if (!arl) {
                        // gelöscht
                        console.warn('arl not found, deleted?', id);
                        return null;
                    }
                    return this.calculationArlFormDataService.getFormByEntity(arlDict[id], null);
                }).filter(isNotNullOrUndefined)
                : [],
        );
        const childArls = new FormArray<arlTableDataArlForm>(
            e.Type === AccountsReceivableLedgerTypeEnum.Group
                ? e.ArlIds.map((id) => {
                      const form = this.getForm(id);
                      if (form) {
                          return form;
                      }
                      const arl = arlDict[id];
                      if (!arl) {
                          // gelöscht
                          console.warn('arl not found, deleted?', id);
                          return null;
                      }
                      this.setFormByEntity(arl, arlDict);
                      return this.getForm(id);
                  }).filter(isNotNullOrUndefined)
                : [],
        );

        this.setForm(
            e.Id,
            this.createFormGroup({
                // Amount: new FormControl<number>(e.Amount, {nonNullable: true}),
                Information: e.Information,
                BookingText: e.BookingText,
                BaseCost: e.BaseCost,
                BaseCostGross: e.BaseCostGross,
                CostAmount: e.CostAmount,
                Quantity: e.Quantity,
                QuantityFormula: e.QuantityFormula,
                QuantityTypeId: e.QuantityTypeId,
                // wird von der arl-calculation-component verwaltet
                childrenVisible: childArlsCalculation.controls.some(c => c.controls.IsVisible.value),
                InheritFromChildren: e.InheritFromChildren,
                ParentId: e.ParentId,
                ARLIds: ArlIdsValue,
                ZuschlaegeResourcen: isNotNullOrUndefined(e.CustomProperties?.ZuschlaegeResourcen) ? e.CustomProperties.ZuschlaegeResourcen : null,
                ZuschlaegeMaterials: isNotNullOrUndefined(e.CustomProperties?.ZuschlaegeMaterials) ? e.CustomProperties.ZuschlaegeMaterials : null,
                ZuschlaegeDefault: isNotNullOrUndefined(e.CustomProperties?.ZuschlaegeDefault) ? e.CustomProperties.ZuschlaegeDefault : null,
                ZuschlaegeEmployee: isNotNullOrUndefined(e.CustomProperties?.ZuschlaegeEmployee) ? e.CustomProperties.ZuschlaegeEmployee : null,
                ZuschlaegeExternalService: isNotNullOrUndefined(e.CustomProperties?.ZuschlaegeExternalService) ? e.CustomProperties.ZuschlaegeExternalService : null,
                ZuschlaegeOther: isNotNullOrUndefined(e.CustomProperties?.ZuschlaegeOther) ? e.CustomProperties.ZuschlaegeOther : null,
                Type: e.Type || null,
                ResourceId: e.ResourceId || null,
                Longtext: e.Longtext || null,
                ShowLongtext: e.ShowLongtext || false,
                Tax: isNotNullOrUndefined(e.Tax) ? e.Tax : null,
                AlreadyPayed: isNotNullOrUndefined(e.AlreadyPayed) ? e.AlreadyPayed : null,
                Date: isNotNullOrUndefined(e.Date) ? moment(e.Date) : null,
                MaterialId: e.MaterialId || null,
                JobSpecificationId: e.JobSpecificationId || null,
                IsOptional: !!e.IsOptional,
                CrossedOut: !!e.CrossedOut,
                HideInGroupSummary: !!e.HideInGroupSummary,
                SalaryBaseCost: e.SalaryBaseCost || 0,
                SupplierId: e.SupplierId,
                StartOnNewPage: e.StartOnNewPage,
                sonstigesBaseCost: parseFloat(((e.BaseCost || 0) - (e.SalaryBaseCost || 0)).toFixed(4)),
                BaseCostAllowances: (e.BaseCostAllowances || []).filter(x => x.percent), // warum eigentlich die mit 0 raus filtern ?
                // childArls: new FormArray<arlTableDataArlForm>(
                //     e.Type === AccountsReceivableLedgerTypeEnum.Group
                //         ? e.ArlIds.map((id) => {
                //               const form = this.getForm(id);
                //               if (form) {
                //                   return form;
                //               }
                //               const arl = arlDict[id];
                //               if (!arl) {
                //                   // gelöscht
                //                   console.warn('arl not found, deleted?', id);
                //                   return null;
                //               }
                //               this.setFormByEntity(arl, arlDict);
                //               return this.getForm(id);
                //           }).filter(isNotNullOrUndefined)
                //         : [],
                // ),
                // childArlsCalculation,
                ItemNumber: e.ItemNumber,
                Id: e.Id,
                ShowQuantityHistory: e.ShowQuantityHistory,
                UseQuantityHistory: e.UseQuantityHistory,
                QuantityHistory: (e.QuantityHistory || []).filter(x => x.quantityDelta).map(x => ({...x, date: x.date ? moment(x.date) : null})),
                lvCalculation: {
                    minutes: e.LVMinutes,
                    SoEk: e.LVSoEk,
                    SoAntEk: e.LVSoAntEk,
                    GEK: e.LVGEK,
                    FlEk: e.LVFlEk,
                    PartieHEur: e.LVPartieHEur,
                    SoEkRabatt: e.LVSoEkRabatt,
                },
                QuantityFormulaUsedVariables: e.QuantityFormula ? mjsHelper.getUsedVariableNames(e.QuantityFormula) : [],
                PriceComparisonId: e.PriceComparisonId,
                ChangedAfterSelectedFromPriceComparison: !!e.ChangedAfterSelectedFromPriceComparison,
                Gaps: (e.Gap ?? []).map(gap =>
                    ({
                        id: (gap?.id ?? null),
                        caption: (gap?.caption ?? null),
                        body: (gap?.body ?? null),
                        indexInLongText: (gap?.indexInLongText ?? 0),
                    })
                ),
                SurchargeOverride: e.SurchargeOverride,
                FixedPrices: e.FixedPrices,
            },
                childArls,
                childArlsCalculation
            )
        );
    }

    public getAmountSumForArls$(rootArlIds: number[]): Observable<currencyValues> {
        const rootForms = rootArlIds.map((id) => ({ id, form: this.getForm(id) }));

        if (rootForms.some((f) => !f.form)) {
            console.error('form not found');
        }
        if (rootForms.some((f) => !f.form) || rootForms.length === 0) {
            return of({
                netto: '0,00',
                brutto: '0,00',
                tax: '0,00',
                netto_number: null,
                brutto_number: null,
                tax_number: null,
            });
        }
        return combineLatest(
            rootForms.map((rootForm) =>
                rootForm.form.controls.ARLIds.valueChanges.pipe(
                    startWith(null),
                    switchMap(() => {
                        // childforms including root form
                        const childForms = this.getChildIds(rootForm.id).map((id) => this.getForm(id));

                        return combineLatest(childForms.map((f) => f.valueChanges.pipe(startWith(null)))).pipe(
                            map(() => {
                                return childForms
                                    .filter((v) => !v.getRawValue().IsOptional && !v.getRawValue().CrossedOut)
                                    .map((cf) => {
                                        const calues = cf.getRawValue();
                                        return {
                                            id: rootForm.id,
                                            Quantity: calues.Quantity,
                                            BaseCost: calues.BaseCost,
                                            BaseCostGross: calues.BaseCostGross,
                                            Tax: calues.Tax,
                                        };
                                    });
                                // const calues = rootForm.form.getRawValue()
                                // return {id: rootForm.id, Quantity: calues.Quantity, BaseCost: calues.BaseCost, Tax: calues.Tax, };
                            }),
                        );
                    }),
                    distinctUntilChanged(compareEqualityAsJsonString),
                ),
            ),
        ).pipe(
            map((v) => v.flat()),
            map((formValues) => {
                if (!formValues.length) {
                    return {
                        netto: '0,00',
                        brutto: '0,00',
                        tax: '0,00',
                        netto_number: null,
                        brutto_number: null,
                        tax_number: null,
                    };
                }
                // const arlDict = {};
                // formValues.forEach((curr) => (arlDict[curr.id] = curr));
                // calculateConsolidatedAmount(arlDict, formValues.map(f => f.id), this.InfoiceFormGroup.getRawValue().DiscountPercentage)

                const brutto = formValues.reduce((prev, curr) => prev + NettoCost(curr.Quantity, curr.BaseCostGross), 0);
                const tax = formValues.reduce((prev, curr) => prev + CalcAccountsReceivableLedgerTaxAmount(curr.Quantity, curr.Tax, curr.BaseCost), 0);
                const netto = formValues.reduce((prev, curr) => prev + NettoCost(curr.Quantity, curr.BaseCost), 0);

                return {
                    netto: formatNumber(netto, this.locale, '1.2-2'),
                    brutto: formatNumber(brutto, this.locale, '1.2-2'),
                    tax: formatNumber(tax, this.locale, '1.2-2') + ' (' + formatNumber(parseFloat(((tax / netto) * 100).toFixed(4)), this.locale, '1.0-2') + ' %)',
                    netto_number: netto,
                    brutto_number: brutto,
                    tax_number: tax,
                };
            }),
        );
    }

    public getAmountSumForArls2$(
        arlIds: number[],
        allowanceCharges: { percentage: number; amount: number; usePercentage: boolean }[],
        calculateBruttoSumAndTaxValuesFromNettoSum: boolean,
        skontoPercentage: number | null = null,
    ): Observable<{
        withAllowanceCharges: currencyValues;
        withoutAllowanceCharges: currencyValues;
        withAllowanceChargesButScontoIgnored: currencyValues;
    }> {
        const emptyCurrencyValue = {
            netto: '0,00',
            brutto: '0,00',
            tax: '0,00',
            netto_number: null,
            brutto_number: null,
            tax_number: null,
        };

        const rootForms = arlIds.map((id) => ({ id, form: this.getForm(id) }));
        if (rootForms.some((f) => !f.form)) {
            console.error('form not found');
            throw new Error('form not found');
        }
        if (rootForms.some((f) => !f.form) || rootForms.length === 0) {
            return of({ withAllowanceCharges: emptyCurrencyValue, withoutAllowanceCharges: emptyCurrencyValue, withAllowanceChargesButScontoIgnored: emptyCurrencyValue });
        }
        return combineLatest(
            rootForms.map((rootForm) =>
                rootForm.form.controls.ARLIds.valueChanges.pipe(
                    startWith(null),
                    switchMap(() => {
                        // childforms including root form
                        const childForms = () =>
                            this.getChildIds(rootForm.id, true)
                                .map((id) => ({ id, form: this.getForm(id) }))
                                .filter((f) => !!f.form);

                        return combineLatest(childForms().map(({ id, form }) => form.valueChanges.pipe(startWith(null)))).pipe(
                            debounceTime(1), // dity fix for performance issue, if one input in lv-calc is changed many valuechanges are thrown and the following function is heavy
                            map(() => {
                                // children müssen hier nochmal geholt werden, für den fall das es ein neuen unterchildren gibt zu testen mit titel in titel und darin eine Position
                                return childForms()
                                    .filter(({ id, form }) => !form.controls.IsOptional.getRawValue() && !form.controls.CrossedOut.getRawValue())
                                    .map(({ id, form }) => {
                                        return {
                                            Id: id,
                                            Quantity: form.controls.Quantity.getRawValue(),
                                            BaseCost: form.controls.BaseCost.getRawValue(),
                                            BaseCostGross: form.controls.BaseCostGross.getRawValue(),
                                            Tax: form.controls.Tax.getRawValue(),
                                            BaseCostAllowances: form.controls.BaseCostAllowances.getRawValue(),
                                            ParentId: form.controls.ParentId.getRawValue(),
                                            Type: form.controls.Type.getRawValue(),
                                            SurchargeOverride: form.controls.SurchargeOverride.getRawValue(),
                                            FixedPrices: form.controls.FixedPrices.getRawValue(),
                                        };
                                    });
                                // const calues = rootForm.form.getRawValue()
                                // return {id: rootForm.id, Quantity: calues.Quantity, BaseCost: calues.BaseCost, Tax: calues.Tax, };
                            }),
                        );
                    }),
                    distinctUntilChanged(compareEqualityAsJsonString),
                ),
            ),
        ).pipe(
            map((v) => v.flat()),
            map((formValues) => {
                if (!formValues.length) {
                    return { withAllowanceCharges: emptyCurrencyValue, withoutAllowanceCharges: emptyCurrencyValue, withAllowanceChargesButScontoIgnored: emptyCurrencyValue };
                }
                // const formValueMap = new Map<number, Pick<arlTableDataArlForm['value'], 'Quantity' | 'BaseCost' | 'Tax' | 'BaseCostAllowances' | 'ParentId' | 'Type'>>(formValues);

                // console.time('getLedgerImportSums')
                const arlAmounts = getLedgerImportSums(formValues, allowanceCharges, calculateBruttoSumAndTaxValuesFromNettoSum, skontoPercentage);
                // console.timeEnd('getLedgerImportSums')

                return {
                    withAllowanceCharges: {
                        netto: formatNumber(arlAmounts.withAllowanceCharges.nettoSum, this.locale, '1.2-2'),
                        brutto: formatNumber(arlAmounts.withAllowanceCharges.bruttoSum, this.locale, '1.2-2'),
                        tax:
                            formatNumber(arlAmounts.withAllowanceCharges.taxSum, this.locale, '1.2-2') +
                            ' (' +
                            formatNumber(parseFloat(((arlAmounts.withAllowanceCharges.taxSum / arlAmounts.withAllowanceCharges.nettoSum) * 100).toFixed(4)), this.locale, '1.0-2') +
                            ' %)',
                        netto_number: arlAmounts.withAllowanceCharges.nettoSum,
                        brutto_number: arlAmounts.withAllowanceCharges.bruttoSum,
                        tax_number: arlAmounts.withAllowanceCharges.taxSum,
                    },
                    withoutAllowanceCharges: {
                        netto: formatNumber(arlAmounts.withoutAllowanceCharges.nettoSum, this.locale, '1.2-2'),
                        brutto: formatNumber(arlAmounts.withoutAllowanceCharges.bruttoSum, this.locale, '1.2-2'),
                        tax:
                            formatNumber(arlAmounts.withoutAllowanceCharges.taxSum, this.locale, '1.2-2') +
                            ' (' +
                            formatNumber(parseFloat(((arlAmounts.withoutAllowanceCharges.taxSum / arlAmounts.withoutAllowanceCharges.nettoSum) * 100).toFixed(4)), this.locale, '1.0-2') +
                            ' %)',
                        netto_number: arlAmounts.withoutAllowanceCharges.nettoSum,
                        brutto_number: arlAmounts.withoutAllowanceCharges.bruttoSum,
                        tax_number: arlAmounts.withoutAllowanceCharges.taxSum,
                    },
                    withAllowanceChargesButScontoIgnored: {
                        netto: formatNumber(arlAmounts.withAllowanceChargesButScontoIgnored.nettoSum, this.locale, '1.2-2'),
                        brutto: formatNumber(arlAmounts.withAllowanceChargesButScontoIgnored.bruttoSum, this.locale, '1.2-2'),
                        tax:
                            formatNumber(arlAmounts.withAllowanceChargesButScontoIgnored.taxSum, this.locale, '1.2-2') +
                            ' (' +
                            formatNumber(parseFloat(((arlAmounts.withAllowanceChargesButScontoIgnored.taxSum / arlAmounts.withAllowanceChargesButScontoIgnored.nettoSum) * 100).toFixed(4)), this.locale, '1.0-2') +
                            ' %)',
                        netto_number: arlAmounts.withAllowanceChargesButScontoIgnored.nettoSum,
                        brutto_number: arlAmounts.withAllowanceChargesButScontoIgnored.bruttoSum,
                        tax_number: arlAmounts.withAllowanceChargesButScontoIgnored.taxSum,
                    },
                };
            }),
        );
    }

    public parentIdsFromForms(id: number) {
        const pIds: number[] = [];
        let pid = this.getForm(id)?.controls.ParentId.getRawValue();
        while (pid) {
            pIds.push(pid);
            pid = this.getForm(pid)?.controls.ParentId.getRawValue();
        }
        return pIds;
    }
    public parentForms(id: number) {
        const forms: arlTableDataArlForm[] = [];
        const c = this.getForm(id)?.controls.ParentId.getRawValue();
        let f = c ? this.getForm(c) : null;
        while (f) {
            forms.push(f);
            f = f.controls.ParentId.getRawValue() ? this.getForm(f.controls.ParentId.getRawValue()) : null;
        }
        return forms;
    }

    public parentIdsFromForms$(id: number) {
        const form = this.getForm(id);
        if (!form) {
         throw 'form not found id:' + id;
        }
        return form.controls.ParentId.valueChanges.pipe(
            startWith(null),
            switchMap(() => {
                const parentIds = this.parentIdsFromForms(id);
                if (parentIds.length === 0) {
                    return of([] as number[]);
                } else {
                    return combineLatest(parentIds.map((p) => this.getForm(p).controls.ParentId.valueChanges.pipe(startWith(null)))).pipe(map(() => this.parentIdsFromForms(id)));
                }
            }),
            // tap((parentIds$) => console.log('parentIds$', parentIds$)),
        );
    }

    public parentBaseCostAllowances$(id: number, includingOwn = false): Observable<Required<arlBaseCostAllowanceFormGroup['value']>[][]> {
        return this.parentIdsFromForms$(id).pipe(
            // tap(parentIds => console.log('### parentBaseCostAllowances$ parentIds', parentIds)),
            switchMap((parentIds) => {
                const ids = (includingOwn ? [id, ...parentIds] : parentIds)
                return ids.length
                    ? combineLatest(
                        ids.map((p) => {
                              const parentForm = this.getForm(p);
                              return parentForm.controls.BaseCostAllowances.valueChanges.pipe(
                                  startWith(null),
                                  map(() => parentForm.controls.BaseCostAllowances.getRawValue()),
                              );
                          }),
                      )
                    : of([]);
            }),
        );
    }
    //  getChildForms(form: arlTableDataArlForm): arlTableDataArlForm[] {
    //     const childArls = form.controls.childArls.controls;
    //     const calcChildArls = form.controls.ca.controls;
    //
    //     if (childArls.length) {
    //         return [form, ...childArls.map(c => this.getChildForms(c)).flat()];
    //     } else {
    //         return [form];
    //     }
    // }

    getChildren$(arlIds: number[], arlDict?: Dictionary<AccountsReceivableLedgerEntity>): Observable<arlTableDataArlForm[]> {
        if (!arlIds.length) {
            return of(null);
        }

        function getChildForms$(form: arlTableDataArlForm): Observable<arlTableDataArlForm[]> {
            return form.controls.childArls.valueChanges.pipe(
                startWith(null),
                map(() => form.controls.childArls.value),
                distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
                switchMap((value) => {
                    // if (value.length) {
                    //     return combineLatest([of(value), ...form.controls.childArls.controls.map(v => getChildForms(v))]);
                    // } else {
                    //     return of([value]);
                    // }
                    if (form.controls.childArls.controls.length) {
                        return combineLatest([of(form), ...form.controls.childArls.controls.map((v) => getChildForms$(v))]).pipe(map((v) => v.flat()));
                    } else {
                        return of([form]);
                    }
                }),
            );
        }

        return combineLatest([
            ...arlIds.map((id) => {
                let form = this.getForm(id);
                if (!form) {
                    if (arlDict) {
                        this.setFormByEntity(arlDict[id], arlDict);
                        form = this.getForm(id);
                    } else {
                        throw new Error('form not found and no arlDictionary provided; ARL Id: ' + id);
                    }
                }
                return getChildForms$(form);
            }),
        ]).pipe(
            map((v) => v.flat()),
            distinctUntilChanged((a, b) => a.length === b.length && a.every((v, i) => v === b[i])),
        );
    }
    setFormByCalculationArlForm(
        calculationArlForm: calculationArlFormGroup,
        missingValues: Pick<Required<arlTableDataArlForm['value']>,
            'childrenVisible'
            | 'InheritFromChildren'
            | 'ARLIds'
            | 'ZuschlaegeResourcen'
            | 'ZuschlaegeMaterials'
            | 'ZuschlaegeDefault'
            | 'ZuschlaegeEmployee'
            | 'ZuschlaegeExternalService'
            | 'ZuschlaegeOther'
            | 'Longtext'
            | 'ShowLongtext'
            | 'Tax'
            | 'AlreadyPayed'
            | 'Date'
            | 'SalaryBaseCost'
            | 'StartOnNewPage'
            | 'sonstigesBaseCost'
            | 'BaseCostAllowances'
            | 'ShowQuantityHistory'
            | 'UseQuantityHistory'
            | 'QuantityHistory'
            | 'lvCalculation'
            | 'PriceComparisonId'
            | 'ChangedAfterSelectedFromPriceComparison'
            | 'Gaps'
            | 'FixedPrices'
        >,
        childArls:FormArray<arlTableDataArlForm>,
        childArlsCalculation:FormArray<calculationArlFormGroup>,
        overrideValues: Partial<arlTableDataArlForm['value']> = {}) {

        if (this.getForm(calculationArlForm.controls.Id.getRawValue())) {
            throw new Error('Form already exists');
        }
        const form = this.createFormGroup(
            {
                Information: calculationArlForm.controls.Information.getRawValue(),
                BookingText: calculationArlForm.controls.BookingText.getRawValue(),
                BaseCost: calculationArlForm.controls.BaseCost.getRawValue(),
                BaseCostGross: calculationArlForm.controls.BaseCostGross.getRawValue(),
                CostAmount: calculationArlForm.controls.CostAmount.getRawValue(),
                Quantity: calculationArlForm.controls.Quantity.getRawValue(),
                QuantityFormula: calculationArlForm.controls.QuantityFormula.getRawValue(),
                QuantityTypeId: calculationArlForm.controls.QuantityTypeId.getRawValue(),
                // wird von der arl-calculation-component verwaltet
                childrenVisible: missingValues.childrenVisible,
                InheritFromChildren: missingValues.InheritFromChildren,
                ParentId: calculationArlForm.controls.ParentId.getRawValue(),
                ARLIds: missingValues.ARLIds,
                ZuschlaegeResourcen: missingValues.ZuschlaegeResourcen,
                ZuschlaegeMaterials: missingValues.ZuschlaegeMaterials,
                ZuschlaegeDefault: missingValues.ZuschlaegeDefault,
                ZuschlaegeEmployee: missingValues.ZuschlaegeEmployee,
                ZuschlaegeExternalService: missingValues.ZuschlaegeExternalService,
                ZuschlaegeOther: missingValues.ZuschlaegeOther,
                Type: calculationArlForm.controls.Type.getRawValue() || null,
                ResourceId: calculationArlForm.controls.ResourceId.getRawValue() || null,
                Longtext: missingValues.Longtext,
                ShowLongtext: missingValues.ShowLongtext,
                Tax: missingValues.Tax,
                AlreadyPayed: missingValues.AlreadyPayed,
                Date: missingValues.Date,
                MaterialId: calculationArlForm.controls.MaterialId.getRawValue() || null,
                JobSpecificationId: calculationArlForm.controls.JobSpecificationId.getRawValue() || null,
                IsOptional: !!calculationArlForm.controls.IsOptional.getRawValue(),
                CrossedOut: !!calculationArlForm.controls.CrossedOut.getRawValue(),
                HideInGroupSummary: !!calculationArlForm.controls.HideInGroupSummary.getRawValue(),
                SalaryBaseCost: missingValues.SalaryBaseCost,
                SupplierId: calculationArlForm.controls.SupplierId.getRawValue(),
                StartOnNewPage: missingValues.StartOnNewPage,
                sonstigesBaseCost: missingValues.sonstigesBaseCost,
                BaseCostAllowances: missingValues.BaseCostAllowances,

                ItemNumber: calculationArlForm.controls.ItemNumber.getRawValue(),
                Id: calculationArlForm.controls.Id.getRawValue(),
                ShowQuantityHistory: missingValues.ShowQuantityHistory,
                UseQuantityHistory: missingValues.UseQuantityHistory,
                QuantityHistory: missingValues.QuantityHistory,
                lvCalculation: missingValues.lvCalculation,
                QuantityFormulaUsedVariables: calculationArlForm.controls.QuantityFormulaUsedVariables.getRawValue(),
                PriceComparisonId: missingValues.PriceComparisonId,
                ChangedAfterSelectedFromPriceComparison: missingValues.ChangedAfterSelectedFromPriceComparison,
                Gaps: missingValues.Gaps,
                SurchargeOverride: calculationArlForm.controls.SurchargeOverride.getRawValue(),
                FixedPrices: missingValues.FixedPrices,

                ...overrideValues,
            },
            childArls,
            childArlsCalculation
        );
        this.setForm(calculationArlForm.controls.Id.getRawValue(), form);

        // console.log(form);
        return form;
    }
    private createFormGroup(e: Required<Omit<arlTableDataArlForm['value'], 'childArls' | 'childArlsCalculation'>>, childArls:FormArray<arlTableDataArlForm>, childArlsCalculation:FormArray<calculationArlFormGroup>): arlTableDataArlForm {
        const form: arlTableDataArlForm = new FormGroup({
                // Amount: new FormControl<number>(e.Amount, {nonNullable: true}),
                Information: new FormControl<string>(e.Information, { nonNullable: true }),
                BookingText: new FormControl<string>(e.BookingText, { nonNullable: true }),
                BaseCost: new FormControl<number>(e.BaseCost, {
                    nonNullable: true,
                    validators: [decimalPriceValidator()],
                }),
                BaseCostGross: new FormControl<number>(e.BaseCostGross, {
                    nonNullable: true,
                    validators: [decimalPriceValidator()],
                }),
                CostAmount: new FormControl<number>(e.CostAmount, {
                    nonNullable: true,
                    validators: [decimalValidator(AccountsReceivableLedgerEntity.CostAmountDecimalPlaces + 2)],
                }),
                Quantity: new FormControl<number>(e.Quantity, { nonNullable: true }),
                QuantityFormula: new FormControl<string>(e.QuantityFormula, { nonNullable: true }),
                QuantityTypeId: new FormControl<number>(e.QuantityTypeId, { nonNullable: true }),
                // wird von der arl-calculation-component verwaltet
                childrenVisible: new FormControl<boolean>({value: e.childrenVisible, disabled: e.ARLIds.length === 0}, { nonNullable: true }),
                InheritFromChildren: new FormControl<boolean>(e.InheritFromChildren, { nonNullable: true }),
                ParentId: new FormControl<number | null>(e.ParentId, { nonNullable: true }),
                ARLIds: new FormControl<number[]>(e.ARLIds, { nonNullable: true }),
                ZuschlaegeResourcen: new FormControl<number>(e.ZuschlaegeResourcen, { nonNullable: true }),
                ZuschlaegeMaterials: new FormControl<number>(e.ZuschlaegeMaterials, { nonNullable: true }),
                ZuschlaegeDefault: new FormControl<number>(e.ZuschlaegeDefault, { nonNullable: true }),
                ZuschlaegeEmployee: new FormControl<number>(e.ZuschlaegeEmployee, { nonNullable: true }),
                ZuschlaegeExternalService: new FormControl<number>(e.ZuschlaegeExternalService, { nonNullable: true }),
                ZuschlaegeOther: new FormControl<number>(e.ZuschlaegeOther, { nonNullable: true }),
                Type: new FormControl<AccountsReceivableLedgerTypeEnum>(e.Type || null, { nonNullable: true }),
                ResourceId: new FormControl<number>(e.ResourceId || null, { nonNullable: true }),
                Longtext: new FormControl<string>(e.Longtext || null, { nonNullable: true }),
                ShowLongtext: new FormControl<boolean>(e.ShowLongtext || false, { nonNullable: true }),
                Tax: new FormControl<number>(isNotNullOrUndefined(e.Tax) ? e.Tax : null, { nonNullable: true }),
                AlreadyPayed: new FormControl<number>(isNotNullOrUndefined(e.AlreadyPayed) ? e.AlreadyPayed : null, { nonNullable: true }),
                Date: new FormControl<Moment>(isNotNullOrUndefined(e.Date) ? moment(e.Date) : null, { nonNullable: true }),
                MaterialId: new FormControl<number>(e.MaterialId || null, { nonNullable: true }),
                JobSpecificationId: new FormControl<number>(e.JobSpecificationId || null, { nonNullable: true }),
                IsOptional: new FormControl<boolean>(!!e.IsOptional, { nonNullable: true }),
                CrossedOut: new FormControl<boolean>(!!e.CrossedOut, { nonNullable: true }),
                HideInGroupSummary: new FormControl<boolean>(!!e.HideInGroupSummary, { nonNullable: false }),
                SalaryBaseCost: new FormControl<number>(e.SalaryBaseCost || 0, {
                    nonNullable: true,
                    validators: [decimalPriceValidator()],
                }),
                SupplierId: new FormControl<number>(e.SupplierId, { nonNullable: true }),
                StartOnNewPage: new FormControl<boolean>(e.StartOnNewPage, { nonNullable: true }),
                sonstigesBaseCost: new FormControl<number>(e.sonstigesBaseCost, decimalPriceValidator()),
                BaseCostAllowances: new FormArray(e.BaseCostAllowances.map(x => getARLBaseCostAllowanceForm({
                    ...x,
                    title: x.title || '',
                    percent: x.percent || null,
                    arlType: x.arlType || null,
                },!!x.arlType ? 'surchargeOverride' : undefined, !!x.arlType))),
                childArls: childArls,
                childArlsCalculation: childArlsCalculation,
                ItemNumber: new FormControl<string>(e.ItemNumber, { nonNullable: false }),
                Id: new FormControl<number>(e.Id, { nonNullable: false }),
                ShowQuantityHistory: new FormControl<boolean>(e.ShowQuantityHistory),
                UseQuantityHistory: new FormControl<boolean>(e.UseQuantityHistory),
                QuantityHistory: new FormArray(e.QuantityHistory.map(a => getARLQuantityHistoryForm({
                    // ...a,
                    date: a.date?.toDate(),
                    quantityDelta: a.quantityDelta,
                    quantityDeltaFormula: a.quantityDeltaFormula,
                }))),
                lvCalculation: new FormGroup({
                    minutes: new FormControl<number>(e.lvCalculation.minutes, { nonNullable: true }),
                    SoEk: new FormControl<number>(e.lvCalculation.SoEk, { nonNullable: true }),
                    SoAntEk: new FormControl<number>(e.lvCalculation.SoAntEk, { nonNullable: true }),
                    GEK: new FormControl<number>(e.lvCalculation.GEK, { nonNullable: true }),
                    FlEk: new FormControl<number>(e.lvCalculation.FlEk, { nonNullable: true }),
                    PartieHEur: new FormControl<number>(e.lvCalculation.PartieHEur, { nonNullable: true }),
                    SoEkRabatt: new FormControl<number>(e.lvCalculation.SoEkRabatt, { nonNullable: true }),
                }),
                QuantityFormulaUsedVariables: new FormControl<string[]>(e.QuantityFormulaUsedVariables, { nonNullable: true }),
                PriceComparisonId: new FormControl<number>(e.PriceComparisonId, { nonNullable: true }),
                ChangedAfterSelectedFromPriceComparison: new FormControl<boolean>(!!e.ChangedAfterSelectedFromPriceComparison, { nonNullable: true }),
                Gaps: new FormArray(
                    (e.Gaps ?? []).map(gap =>
                        new FormGroup({
                            id: new FormControl<number | null>(gap?.id ?? null),
                            caption: new FormControl<string | null>(gap?.caption ?? null),
                            body: new FormControl<string | null>(gap?.body ?? null),
                            indexInLongText: new FormControl<number>(gap?.indexInLongText ?? 0, { nonNullable: true }),
                        })
                    )
                ),
                SurchargeOverride: new FormControl<number>(e.SurchargeOverride, { nonNullable: true }),
                FixedPrices: new FormControl<boolean>(e.FixedPrices, { nonNullable: true }),
            },
        );
        if (this.createNewFormsDisabled) {
            form.disable();
        }
        return form;
    }
}
